import { Context } from 'access-control/Context.enum';
import { hasPermissionInContext } from 'access-control/PermissionChecker';
import { Permission } from 'lib/enum/permission.enum';
import { UserStatus } from 'lib/enum/user-status.enum';
import { createAssigneeRoles } from 'lib/utils/assigneRoles.utils';
import { getUuidFromIri } from 'lib/utils/iri';
import { BusinessAccountMembership, User, UserProductItemRole, UserProductRole } from 'services';
import { getFirstChar } from 'lib/utils/string.utils';

export const getUsersWithPermission = (
    memberships: BusinessAccountMembership[],
    usersDict: Record<string, User>,
    userProductRoles: UserProductRole[],
    userProductItemRoles: UserProductItemRole[],
    permission: Permission,
): User[] =>
    memberships
        .filter(membership => {
            const foundUserProductRole = userProductRoles.find(({ user: userIri }) => userIri === membership.user);
            const foundUserProductItemRole = userProductItemRoles.find(
                ({ user: userIri }) => userIri === membership.user,
            );
            const assigneeRoles = createAssigneeRoles(membership!, foundUserProductRole, foundUserProductItemRole);

            return hasPermissionInContext(assigneeRoles, Context.ProductItem, permission);
        })
        .map(memberships => usersDict[getUuidFromIri(memberships.user)])
        .filter(user => !!user);

export const getUserStatus = (user: User): UserStatus => {
    if (user.disabled) {
        return UserStatus.Disabled;
    }

    if (user.activated) {
        return UserStatus.Active;
    }

    return UserStatus.Inactive;
};

export const getUserInitials = (
    user: {
        givenName?: string | null;
        familyName?: string | null;
        email: string;
    } | null,
): string => {
    if (!user) {
        return '';
    }
    return user.givenName && user.familyName
        ? `${getFirstChar(user.givenName)}${getFirstChar(user.familyName)}`
        : user.email.substring(0, 2);
};

export const getUserDisplayName = (user: User | null): string => {
    if (user?.givenName) {
        return `${user.givenName} ${user.familyName || ''}`.trim();
    }
    return user?.email ?? '';
};
